/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/broadcastGroupsApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API calls for Voicemail Broadcast Groups (fetch list, fetch by ID, create, update, delete).
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { VoicemailBroadcastGroup, BroadcastGroupPayload } from '../types';

export interface FetchBroadcastGroupsParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

export interface BroadcastGroupsListResponse {
  success: boolean;
  data: VoicemailBroadcastGroup[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BroadcastGroupDetailResponse {
  success: boolean;
  data: VoicemailBroadcastGroup;
  message?: string;
}

export async function fetchBroadcastGroups(params: FetchBroadcastGroupsParams = {}): Promise<BroadcastGroupsListResponse> {
  const queryParams = new URLSearchParams({
    page: (params.page || 1).toString(),
    limit: (params.limit || 10).toString(),
    sortBy: params.sortBy || 'name',
    sortOrder: params.sortOrder || 'asc',
    ...(params.search && { search: params.search })
  });

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/voicemails-broadcast-groups?${queryParams}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch voicemail broadcast groups');
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch voicemail broadcast groups');
  }

  return result;
}

export async function fetchBroadcastGroupById(id: string | number): Promise<VoicemailBroadcastGroup> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/voicemails-broadcast-groups/${id}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch voicemail broadcast group');
  }

  const result: BroadcastGroupDetailResponse = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch voicemail broadcast group');
  }

  return result.data;
}

export async function createBroadcastGroup(payload: BroadcastGroupPayload): Promise<VoicemailBroadcastGroup> {
  const response = await fetch(
    buildApiUrl('/api/telephony/voicemails-broadcast-groups'),
    {
      method: 'POST',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to create voicemail broadcast group');
  }

  const result: BroadcastGroupDetailResponse = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to create voicemail broadcast group');
  }

  return result.data;
}

export async function updateBroadcastGroup(id: string | number, payload: BroadcastGroupPayload): Promise<VoicemailBroadcastGroup> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/voicemails-broadcast-groups/${id}`),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to update voicemail broadcast group');
  }

  const result: BroadcastGroupDetailResponse = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update voicemail broadcast group');
  }

  return result.data;
}

export async function deleteBroadcastGroup(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/voicemails-broadcast-groups/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to delete voicemail broadcast group');
  }
}


